import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  JSXElement,
  onCleanup,
  onMount,
  Show,
  untrack,
} from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import Icon, { CONNECT_ICON_FONT_URL } from '../Icon/Icon';
import { DATA_THEME_ATTRIBUTE, DEFAULT_MODUS_THEME } from '../constants';
import { injectInRenderContext, waitForWebComponentHydrated } from '../util';
import MenuItemTree from './MenuItemTree';
import { MenuContext } from './side-navigation-context';
import {
  collapseInlineSubmenu,
  type ModusWcTreeItemEl,
} from './side-navigation-dom';
import {
  collectParentValues,
  deriveSelectedValue,
  resolveEndActionEntry,
} from './side-navigation-selection';
import type {
  ItemSelectDetail,
  SideNavigationColorScheme,
  SideNavigationMenuItem,
  SideNavigationProps,
} from './side-navigation-types';
import sideNavigationStyles from '../../mc-custom-css/mc-side-navigation.scss?inline';

export type {
  EndActionEntry,
  ItemSelectDetail,
  SideNavigationMenuItem,
  SideNavigationColorScheme,
  SideNavigationMode,
  SideNavigationProps,
  SideNavigationSubItem,
} from './side-navigation-types';

const SideNavigation: Component<SideNavigationProps> = (props): JSXElement => {
  const collapseOutside = (): boolean => props.collapseOnClickOutside !== false;
  const expanded = (): boolean => props.expanded === true;
  const maxW = (): string => props.maxWidth ?? '256px';
  const mode = (): SideNavigationProps['mode'] => props.mode ?? 'overlay';
  const colorScheme = (): SideNavigationColorScheme =>
    props.colorScheme ?? 'primary';
  const target = (): string => props.targetContent ?? '';
  const customClass = (): string => props.customClass ?? '';
  const hasMenuItems = (): boolean => (props.menuItems?.length ?? 0) > 0;

  const [htmlTheme, setHtmlTheme] = createSignal<string>(DEFAULT_MODUS_THEME);
  const [componentReady, setComponentReady] = createSignal(false);

  let rootDivRef: HTMLDivElement | undefined;

  onMount(() => {
    setHtmlTheme(
      document.documentElement.getAttribute(DATA_THEME_ATTRIBUTE) ??
        DEFAULT_MODUS_THEME
    );

    let cancelled = false;
    customElements.whenDefined('tc-side-navigation').then(() => {
      if (cancelled || !rootDivRef) return;
      injectInRenderContext(
        rootDivRef,
        'mc-side-navigation-style',
        sideNavigationStyles
      );
    });

    if (rootDivRef) {
      waitForWebComponentHydrated(rootDivRef, {
        selector: 'modus-wc-side-navigation',
        tagName: 'modus-wc-side-navigation',
        onHydrated: () => setComponentReady(true),
        registerCleanup: onCleanup,
      });
    }

    onCleanup(() => {
      cancelled = true;
    });

    const observer = new MutationObserver(() => {
      setHtmlTheme(
        document.documentElement.getAttribute(DATA_THEME_ATTRIBUTE) ??
          DEFAULT_MODUS_THEME
      );
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [DATA_THEME_ATTRIBUTE],
    });
    onCleanup(() => {
      observer.disconnect();
    });
  });

  const selectedValue = createMemo(() =>
    deriveSelectedValue(props.menuItems ?? [])
  );

  const [stableItems, setStableItems] = createStore<SideNavigationMenuItem[]>(
    untrack(() => props.menuItems ?? [])
  );

  createEffect(() => {
    setStableItems(
      reconcile(props.menuItems ?? [], {
        key: 'value',
        merge: true,
      })
    );
  });

  const endActionEntry = createMemo(() =>
    resolveEndActionEntry(props.menuItems ?? [])
  );

  const parentValues = createMemo(() =>
    collectParentValues(props.menuItems ?? [])
  );

  const [menuWrapper, setMenuWrapper] = createSignal<
    HTMLDivElement | undefined
  >(undefined);

  createEffect(() => {
    const wrapper = menuWrapper();
    if (!wrapper) return;

    const handler = (e: Event): void => {
      const detail = (e as CustomEvent<ItemSelectDetail>).detail;
      if (!detail || typeof detail.value !== 'string') return;

      const { value, selected } = detail;

      if (parentValues().has(value)) {
        wrapper
          .querySelectorAll('modus-wc-tree-item')
          .forEach((el: Element) => {
            const treeItemEl = el as ModusWcTreeItemEl;
            const elValue = treeItemEl.value;
            if (
              elValue !== value &&
              elValue !== undefined &&
              parentValues().has(elValue) &&
              typeof treeItemEl.collapseSubmenu === 'function'
            ) {
              collapseInlineSubmenu(treeItemEl as HTMLElement);
              void treeItemEl.collapseSubmenu();
            }
          });
        return;
      }

      props.onItemSelect?.(value, selected);
    };

    wrapper.addEventListener('itemSelect', handler);
    onCleanup(() => {
      wrapper.removeEventListener('itemSelect', handler);
    });
  });

  return (
    <MenuContext.Provider value={{ selectedValue }}>
      <div
        ref={(el) => (rootDivRef = el)}
        data-theme={htmlTheme()}
        data-nav-color-scheme={colorScheme()}
      >
        <link rel="stylesheet" href={CONNECT_ICON_FONT_URL} />
        <modus-wc-side-navigation
          collapse-on-click-outside={collapseOutside()}
          expanded={expanded()}
          max-width={maxW()}
          mode={mode()}
          {...(target() ? { 'target-content': target() } : {})}
          custom-class={customClass()}
        >
          <Show when={!!props.logoUrl}>
            <div
              class={`mc-nav-header${expanded() ? ' mc-nav-header-visible' : ''}`}
            >
              <button
                type="button"
                class="mc-nav-close-btn"
                aria-label="Close"
                onClick={() => props.onNavigationClose?.()}
              >
                <Icon
                  icon="tc-icon-content-close"
                  size="icon-medium"
                  skipFontLink
                />
              </button>
              {mode() === 'overlay' && props.onLogoClick ? (
                <button
                  type="button"
                  class="mc-nav-logo-btn"
                  aria-label="Trimble Connect"
                  onClick={() => props.onLogoClick?.()}
                >
                  <img
                    class="mc-nav-logo-img"
                    src={props.logoUrl}
                    alt="Trimble Connect"
                  />
                </button>
              ) : (
                <img
                  class="mc-nav-logo-img"
                  src={props.logoUrl}
                  alt="Trimble Connect"
                />
              )}
            </div>
          </Show>

          <div ref={(el) => props.containerRef?.(el)} />

          <Show when={hasMenuItems()}>
            <div
              ref={(el: HTMLDivElement) => setMenuWrapper(el)}
              style={{
                display: 'contents',
                visibility: componentReady() ? 'visible' : 'hidden',
              }}
            >
              <MenuItemTree
                items={stableItems}
                ariaLabel={props.menuAriaLabel ?? 'Menu'}
                expanded={expanded()}
                mode={mode()}
                endAction={endActionEntry}
                onItemAction={props.onItemAction}
              />
            </div>
          </Show>
        </modus-wc-side-navigation>
      </div>
    </MenuContext.Provider>
  );
};

export default SideNavigation;
